import React, { useEffect, useRef, useState } from "react";
import { Alert, Checkbox, InputNumber, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { usePostRotateExternalSecretMutation } from "../../../redux/externals/externalsAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import type { ExternalDetail, RotatedExternalSecret } from "../../../interfaces/External.interface";
import apiClientErrorMessage from "./apiClientErrors";

/** LiftCloud's default grace period for the old secret: 24 hours. */
export const DEFAULT_GRACE_SECONDS = 86400;
/** LiftCloud's longest grace period: 7 days. */
export const MAX_GRACE_SECONDS = 604800;

interface ApiClientRotateModalProps {
  /** The API client whose secret is rotated, or null when the modal is closed. */
  client: ExternalDetail | null;
  onClose: () => void;
  /** Called with the answer, which carries the new secret once. */
  onRotated: (client: ExternalDetail, rotated: RotatedExternalSecret) => void;
}

/** Issues a new client secret; the old one works for the grace period. */
function ApiClientRotateModal({ client, onClose, onRotated }: ApiClientRotateModalProps) {
  const [t] = useTranslation("global");
  const [grace, setGrace] = useState<number | null>(DEFAULT_GRACE_SECONDS);
  const [revoke, setRevoke] = useState(false);
  const [error, setError] = useState("");
  const [rotate] = usePostRotateExternalSecretMutation();
  // Set synchronously: a second submit would issue a second secret.
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setGrace(DEFAULT_GRACE_SECONDS);
    setRevoke(false);
    setError("");
  }, [client?.id]);

  const graceValid =
    grace !== null && Number.isInteger(grace) && grace >= 0 && grace <= MAX_GRACE_SECONDS;

  const submit = async () => {
    if (savingRef.current || client === null) return;
    if (!graceValid || grace === null) {
      setError(t("apiClients.graceInvalid", { max: MAX_GRACE_SECONDS }));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const token = await GETJwtToken();
      const rotated = await rotate({
        token,
        id: client.id,
        graceSeconds: grace,
        revokeTokens: revoke,
      }).unwrap();
      onRotated(client, rotated);
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={client !== null}
      title={t("apiClients.rotateTitle", { name: client?.name ?? "" })}
      okText={t("apiClients.rotateConfirm")}
      cancelText={t("apiClients.cancel")}
      onOk={submit}
      onCancel={saving ? undefined : onClose}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      confirmLoading={saving}
      okButtonProps={{ "data-testid": "api-client-rotate-submit" } as Record<string, unknown>}
      cancelButtonProps={
        { "data-testid": "api-client-rotate-cancel", disabled: saving } as Record<string, unknown>
      }
      destroyOnClose
    >
      <div data-testid="api-client-rotate-modal">
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className="mb-3"
            data-testid="api-client-rotate-error"
          />
        )}
        <p>{t("apiClients.rotateHelp")}</p>
        <span className="drawer__text">{t("apiClients.graceSeconds")}</span>
        <div style={{ marginTop: 6 }}>
          <InputNumber
            data-testid="api-client-rotate-grace"
            min={0}
            max={MAX_GRACE_SECONDS}
            precision={0}
            value={grace}
            onChange={(value) => setGrace(value)}
            addonAfter={t("apiClients.seconds")}
          />
        </div>
        <p className="mt-1">{t("apiClients.graceHelp")}</p>
        <Checkbox
          data-testid="api-client-rotate-revoke"
          checked={revoke}
          onChange={(event) => setRevoke(event.target.checked)}
        >
          {t("apiClients.revokeTokens")}
        </Checkbox>
      </div>
    </Modal>
  );
}

export default ApiClientRotateModal;
